import { useState, useCallback, useMemo } from "react";
import moment from "moment";
import { adminSearchService } from "../../../services/adminSearch";
import { makeCsv, downloadCsv } from "../utils/csvUtils";
import { buildFiltersPayload } from "../utils/filterPayloadBuilder";
import { extractCellValue } from "../components/UsersView/UserTableColumns";

/**
 * Hook for exporting users to CSV
 * @param {Object} params
 * @param {Object} params.filters - Filters object (same shape as API: userInput, subscriptionType, gender, etc.)
 * @param {Array<string>} params.visibleColumnDefs - Array of visible column keys
 * @returns {Object} Export state and handler
 */
export function useCsvExport({ filters, visibleColumnDefs }) {
  const [exporting, setExporting] = useState(false);

  const csvHeaders = useMemo(() => {
    return visibleColumnDefs
      .filter((col) => col.key !== "actions") // Exclude actions column from CSV
      .map((col) => ({
        key: col.key,
        label: col.label,
      }));
  }, [visibleColumnDefs]);

  const exportCsv = useCallback(async () => {
    if (exporting) return;

    setExporting(true);
    try {
      const pageSize = 500;
      let page = 1;
      let all = [];
      let totalCount = 0;

      const filtersPayload = buildFiltersPayload(filters);
      const requestFilters = {
        ...filtersPayload,
        ...(filters.userInput?.trim()
          ? { userInput: filters.userInput.trim() }
          : {}),
        ...(Array.isArray(filters.subscriptionType) &&
        filters.subscriptionType.length > 0
          ? { subscriptionType: filters.subscriptionType }
          : {}),
      };

      // Fetch all pages
      for (;;) {
        const res = await adminSearchService.adminSearchUsers({
          page,
          limit: pageSize,
          filters: requestFilters,
        });
        const users = Array.isArray(res?.users) ? res.users : [];
        totalCount = Number(res?.total || 0);
        all = all.concat(users);

        if (!users.length || all.length >= totalCount) break;
        page += 1;
      }

      // Transform users to CSV rows using the same columns as the table
      const rows = all.map((u) => {
        const row = {};
        visibleColumnDefs
          .filter((col) => col.key !== "actions")
          .forEach((col) => {
            row[col.key] = extractCellValue(u, col.key);
          });
        return row;
      });

      // Generate and download CSV
      const csv = makeCsv({ rows, headers: csvHeaders });
      const stamp = moment().format("YYYY-MM-DD-HH:mm");
      downloadCsv({ csv, filename: `admin-users-${stamp}.csv` });
    } catch (e) {
      console.error("Failed to export CSV:", e);
      // Could show a toast here if needed
    } finally {
      setExporting(false);
    }
  }, [filters, exporting, visibleColumnDefs, csvHeaders]);

  return { exporting, exportCsv };
}
